import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { getConfig } from "@/lib/config";
import { startPortalSocketEngine, type EngineSocket, type PortalEvent } from "@/lib/portal-socket-engine";

/**
 * Member-portal realtime layer (#172).
 *
 * One long-lived WebSocket to ``/ws/portal/me/`` shared by every realtime
 * portal feature. The backend (``tenant_portal.realtime``) broadcasts typed
 * events to the member's user/tenant/site scope; the consumer forwards each
 * as ``{ type, ...payload }``. Features subscribe by type via
 * ``usePortalEvent`` — announcements is the first; doorbell rings, support
 * replies, booking changes plug in the same way, all on this one socket
 * (no second connection per tab).
 *
 * The connection lifecycle — capped-backoff reconnect, token handling
 * (re-read per connect so a refresh mid-outage is picked up), and the #189
 * staleness watchdog that recycles a half-open socket when the server's
 * 20 s keepalive pings stop arriving — lives in ``portal-socket-engine.ts``
 * (framework-free, unit-tested). This provider wires the engine to React
 * context and the real WebSocket. Mount it once inside the authenticated
 * shell, where a token is present.
 *
 * A synthetic ``"reconnect"`` event is dispatched on every successful
 * reopen *after the first* — subscribers use it to refetch and catch up on
 * anything missed while the socket was down.
 */

export { RECONNECT_EVENT } from "@/lib/portal-socket-engine";
export type { PortalEvent } from "@/lib/portal-socket-engine";

type Handler = (event: PortalEvent) => void;

interface PortalSocketContextValue {
  subscribe: (type: string, handler: Handler) => () => void;
}

const PortalSocketContext = createContext<PortalSocketContextValue | null>(null);

export function PortalSocketProvider({ children }: { children: ReactNode }) {
  // type -> set of handlers. A ref (not state) so (un)subscribing never
  // re-renders or tears down the socket.
  const handlersRef = useRef<Map<string, Set<Handler>>>(new Map());

  useEffect(() => {
    const engine = startPortalSocketEngine({
      resolveUrl: resolveWsUrl,
      // The DOM lib types handlers as (this: WebSocket, ev: Event) => any —
      // a superset of the engine's zero/data-only signatures, hence the cast.
      createSocket: (url) => new WebSocket(url) as unknown as EngineSocket,
      dispatch: (event) => {
        const set = handlersRef.current.get(event.type);
        if (!set) return;
        for (const h of set) h(event);
      },
    });
    return () => engine.stop();
  }, []);

  const subscribe = (type: string, handler: Handler) => {
    let set = handlersRef.current.get(type);
    if (!set) {
      set = new Set();
      handlersRef.current.set(type, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
      if (set && set.size === 0) handlersRef.current.delete(type);
    };
  };

  return (
    <PortalSocketContext.Provider value={{ subscribe }}>
      {children}
    </PortalSocketContext.Provider>
  );
}

/**
 * Subscribe to a portal realtime event for the lifetime of the component.
 * Pass ``RECONNECT_EVENT`` to also run on socket reconnect (catch-up).
 *
 *   usePortalEvent("announcements_changed", () => invalidate(["announcements"]));
 *   usePortalEvent("doorbell_ring", (e) => notifyRing(e.notification_id));
 *
 * No-op when rendered outside a ``PortalSocketProvider`` (e.g. unauthed
 * routes) so feature components can call it unconditionally.
 */
export function usePortalEvent(type: string, handler: Handler): void {
  const ctx = useContext(PortalSocketContext);
  // Keep the latest handler in a ref so re-renders don't churn the
  // subscription (and callers needn't memoise their handler).
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!ctx) return;
    const unsubscribe = ctx.subscribe(type, (event) => handlerRef.current(event));
    return unsubscribe;
  }, [ctx, type]);
}

function resolveWsUrl(): string | null {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem("access_token");
  if (!token) return null;
  // API_BASE_URL is e.g. ``https://api.nodus.foo/api`` (see lib/config) —
  // swap the scheme to ws(s) and append the portal WS path.
  const base = getConfig().API_BASE_URL?.replace(/^http/i, "ws");
  if (!base) return null;
  return `${base}/ws/portal/me/?token=${encodeURIComponent(token)}`;
}
